<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  application: {
    type: Object,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'update', 'regenerate-secret']);
const { t } = useI18n();

const formData = ref({
  redirect_uri: props.application.redirect_uri,
});

const availableScopes = [
  {
    value: 'profile',
    labelKey: 'OAUTH_APPLICATIONS.SCOPES.PROFILE',
    descriptionKey: 'OAUTH_APPLICATIONS.SCOPES.DESCRIPTIONS.PROFILE',
  },
  {
    value: 'accounts',
    labelKey: 'OAUTH_APPLICATIONS.SCOPES.ACCOUNTS',
    descriptionKey: 'OAUTH_APPLICATIONS.SCOPES.DESCRIPTIONS.ACCOUNTS',
  },
  {
    value: 'read',
    labelKey: 'OAUTH_APPLICATIONS.SCOPES.READ',
    descriptionKey: 'OAUTH_APPLICATIONS.SCOPES.DESCRIPTIONS.READ',
  },
  {
    value: 'write',
    labelKey: 'OAUTH_APPLICATIONS.SCOPES.WRITE',
    descriptionKey: 'OAUTH_APPLICATIONS.SCOPES.DESCRIPTIONS.WRITE',
  },
];

const selectedScopes = ref(
  typeof props.application.scopes === 'string'
    ? props.application.scopes.split(' ').filter(Boolean)
    : Array.isArray(props.application.scopes)
      ? props.application.scopes
      : []
);

const handleSubmit = () => {
  if (!selectedScopes.value.length) return;

  emit('update', {
    id: props.application.id,
    redirect_uri: formData.value.redirect_uri,
    scopes: selectedScopes.value.join(' '),
  });
};

const handleRegenerateSecret = () => {
  emit('regenerate-secret', props.application.id);
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('OAUTH_APPLICATIONS.UPDATE.TITLE')"
      :header-content="$t('OAUTH_APPLICATIONS.UPDATE.NAME_READONLY')"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleSubmit">
      <div class="w-full">
        <label>
          {{ $t('OAUTH_APPLICATIONS.ITEM.NAME') }}
          <input
            :value="application.name"
            type="text"
            name="name"
            disabled
          />
        </label>

        <label>
          {{ $t('OAUTH_APPLICATIONS.CREATE.FORM.REDIRECT_URI.LABEL') }}
          <input
            v-model="formData.redirect_uri"
            type="url"
            name="redirect_uri"
            :placeholder="
              $t('OAUTH_APPLICATIONS.CREATE.FORM.REDIRECT_URI.PLACEHOLDER')
            "
            required
          />
        </label>
        <p class="mb-4 -mt-2 text-xs text-n-slate-11">
          {{ $t('OAUTH_APPLICATIONS.CREATE.FORM.REDIRECT_URI.HELP') }}
        </p>

        <label class="mb-2">
          {{ $t('OAUTH_APPLICATIONS.CREATE.FORM.SCOPES.LABEL') }}
        </label>
        <div class="flex flex-col gap-2.5 mb-4">
          <div
            v-for="scope in availableScopes"
            :key="scope.value"
            class="flex items-center"
          >
            <input
              :id="`edit-scope-${scope.value}`"
              v-model="selectedScopes"
              type="checkbox"
              :value="scope.value"
              name="scopes"
              class="mr-2"
            />
            <label :for="`edit-scope-${scope.value}`" class="text-sm !mb-0">
              {{ t(scope.labelKey) }}
              <span class="text-n-slate-11">
                ({{ t(scope.descriptionKey) }})
              </span>
            </label>
          </div>
        </div>

        <div
          class="flex flex-col gap-3 p-4 mb-4 rounded-lg border border-n-amber-6 bg-n-amber-3"
        >
          <div>
            <p class="text-sm font-medium text-n-amber-12">
              {{ $t('OAUTH_APPLICATIONS.UPDATE.REGENERATE_SECRET_TITLE') }}
            </p>
            <p class="mt-1 text-sm text-n-amber-11">
              {{ $t('OAUTH_APPLICATIONS.UPDATE.REGENERATE_SECRET_WARNING') }}
            </p>
          </div>
          <div>
            <NextButton
              type="button"
              amber
              sm
              icon="i-lucide-key-round"
              :label="$t('OAUTH_APPLICATIONS.UPDATE.REGENERATE_SECRET_BUTTON')"
              :is-loading="isSubmitting"
              @click="handleRegenerateSecret"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('OAUTH_APPLICATIONS.CREATE.FORM.CANCEL')"
          @click.prevent="emit('close')"
        />
        <NextButton
          type="submit"
          :label="$t('OAUTH_APPLICATIONS.UPDATE.FORM.SUBMIT')"
          :disabled="!selectedScopes.length || isSubmitting"
          :is-loading="isSubmitting"
        />
      </div>
    </form>
  </div>
</template>
